<script lang="ts">
/** The key the "Without contract type" option stands for; every other key is a type's uid. */
export const WITHOUT_CONTRACT_TYPE = 'without';

/** The picked keys as a prompt search filter; undefined when none is picked (any type). */
export function contractTypeFilterFromKeys(
  keys: string[]
): { contractTypeUids: string[]; withoutContractType: boolean } | undefined {
  if (!keys.length) return undefined;
  return {
    contractTypeUids: keys.filter(key => key !== WITHOUT_CONTRACT_TYPE),
    withoutContractType: keys.includes(WITHOUT_CONTRACT_TYPE),
  };
}
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import GaiaPopover from '../../base/GaiaPopover.vue';
import GaiaFilterChip from '../../composite/GaiaFilterChip.vue';
import GaiaFilterValuePanel, {
  type GaiaFilterValue,
} from '../../composite/GaiaFilterValuePanel.vue';

/**
 * A multi-select filter over the organisation's contract types: a filter chip
 * naming the picks, and a searchable checkbox panel behind it. Picks are uids,
 * plus `WITHOUT_CONTRACT_TYPE` when `withoutOption` offers it.
 */
const props = withDefaults(
  defineProps<{
    /** Offer "Without contract type" first, for things that can have none. */
    withoutOption?: boolean;
  }>(),
  { withoutOption: false }
);

/** The picked keys; empty means any. */
const picked = defineModel<string[]>({ required: true });

const contractTypeStore = useContractTypeStore();

const options = computed((): { key: string; label: string }[] => [
  ...(props.withoutOption ? [{ key: WITHOUT_CONTRACT_TYPE, label: 'Without contract type' }] : []),
  ...contractTypeStore.sortedContractTypes.map(type => ({ key: type.uid, label: type.name })),
]);

const labels = computed(() => new Map(options.value.map(option => [option.key, option.label])));

const open = ref(false);
const search = ref('');

const panelValues = computed((): GaiaFilterValue[] => {
  const term = search.value.trim().toLowerCase();
  return options.value
    .filter(option => !term || option.label.toLowerCase().includes(term))
    .map(option => ({ ...option, checked: picked.value.includes(option.key) }));
});

/** The chip names the first two picks; the rest are "+N". */
const pickedLabels = computed((): string[] =>
  picked.value.map(key => labels.value.get(key) ?? key)
);

function toggle(key: string): void {
  picked.value = picked.value.includes(key)
    ? picked.value.filter(k => k !== key)
    : [...picked.value, key];
}

function removeLabel(label: string): void {
  const key = picked.value.find(k => (labels.value.get(k) ?? k) === label);
  if (key) toggle(key);
}

// The panel lists the store's types; a page that shows the filter needs them loaded.
void contractTypeStore.ensureLoaded();
</script>

<template>
  <!-- Manual: the chip's own toggle opens it; a click trigger would toggle it back shut. -->
  <gaia-popover trigger="manual" :show="open" placement="bottom-end" @clickoutside="open = false">
    <template #trigger>
      <gaia-filter-chip
        field="Contract type"
        :values="pickedLabels.slice(0, 2)"
        :overflow="pickedLabels.length > 2 ? `+${pickedLabels.length - 2}` : undefined"
        :active="open"
        @toggle="open = !open"
        @remove="picked = []"
        @remove-value="removeLabel"
      />
    </template>
    <gaia-filter-value-panel
      v-model:search="search"
      :values="panelValues"
      search-placeholder="Search contract types"
      :picked-label="picked.length ? `${picked.length} selected` : undefined"
      @toggle="toggle"
      @clear="picked = []"
    />
  </gaia-popover>
</template>
